import { fontFamilyCss } from '../fontStack.js'
import { kerns } from './chartText.js'

/**
 * Chart layout primitives (chartLayout.js) → a ProseMirror DOMOutputSpec
 * for one <svg> in the SVG namespace, sized in points with a viewBox in
 * points, so it scales with the page exactly like Word's drawing does.
 * Spec arrays (not DOM nodes) keep renderHTML and the unit tests DOM-free.
 */

const SVG = 'http://www.w3.org/2000/svg'

/** a:prstDash → dash pattern in multiples of the line width. */
const DASHES = {
  solid: null,
  sysDot: [1, 1],
  sysDash: [3, 1],
  sysDashDot: [3, 1, 1, 1],
  sysDashDotDot: [3, 1, 1, 1, 1, 1],
  dot: [1, 3],
  dash: [4, 3],
  dashDot: [4, 3, 1, 3],
  lgDash: [8, 3],
  lgDashDot: [8, 3, 1, 3],
  lgDashDotDot: [8, 3, 1, 3, 1, 3],
}

const CAPS = { rnd: 'round', sq: 'square', flat: 'butt' }

let clipSequence = 0

const round = (value) => Math.round(value * 1000) / 1000

function stroke(line) {
  if (!line) return { stroke: 'none' }
  const attrs = {
    stroke: line.color,
    'stroke-width': round(line.width),
    'stroke-linecap': CAPS[line.cap] ?? 'butt',
    'stroke-linejoin': 'round',
  }
  const dash = DASHES[line.dash]
  if (dash) {
    // Office draws round-capped dots as dots: the cap supplies the length.
    const capped = line.cap === 'rnd' || line.cap === 'sq'
    attrs['stroke-dasharray'] = dash.map((part, index) => round(Math.max(index % 2 === 0 && capped ? part - 1 : part, index % 2 === 0 ? 0.001 : part + (capped ? 1 : 0)) * line.width)).join(' ')
  }
  return attrs
}

function textAttrs(item) {
  const style = item.style
  const attrs = {
    x: round(item.x),
    y: round(item.y),
    'font-family': fontFamilyCss(style.font) || 'sans-serif',
    'font-size': round(style.size),
    fill: style.color ?? '#000000',
    style: kerns(style) ? 'white-space: pre' : 'white-space: pre; font-kerning: none',
  }
  if (style.bold) attrs['font-weight'] = 'bold'
  if (style.italic) attrs['font-style'] = 'italic'
  if (item.anchor && item.anchor !== 'start') attrs['text-anchor'] = item.anchor
  const decorations = [style.underline ? 'underline' : null, style.strike ? 'line-through' : null].filter(Boolean)
  if (decorations.length) attrs['text-decoration'] = decorations.join(' ')
  if (item.rotate) attrs.transform = `rotate(${round(item.rotate)} ${round(item.ox ?? item.x)} ${round(item.oy ?? item.y)})`
  // What the text edits (RED-317 PR 3d-A): the inline editor finds its box by it.
  if (item.target) attrs['data-target'] = item.target
  return attrs
}

function markerSpec(item) {
  const r = item.size / 2
  const paint = { fill: item.fill ?? 'none', ...stroke(item.line) }
  const { x, y } = item
  switch (item.symbol) {
    case 'circle':
    case 'dot':
      return [`${SVG} circle`, { cx: round(x), cy: round(y), r: round(item.symbol === 'dot' ? r / 2 : r), ...paint }]
    case 'square':
      return [`${SVG} rect`, { x: round(x - r), y: round(y - r), width: round(2 * r), height: round(2 * r), ...paint }]
    case 'diamond':
      return [`${SVG} polygon`, { points: [[x, y - r], [x + r, y], [x, y + r], [x - r, y]].map((p) => p.map(round).join(',')).join(' '), ...paint }]
    case 'triangle':
      return [`${SVG} polygon`, { points: [[x, y - r], [x + r, y + r], [x - r, y + r]].map((p) => p.map(round).join(',')).join(' '), ...paint }]
    case 'x':
    case 'star':
    case 'plus':
    case 'dash': {
      const d =
        item.symbol === 'plus'
          ? `M${x - r},${y}H${x + r}M${x},${y - r}V${y + r}`
          : item.symbol === 'dash'
            ? `M${x - r},${y}H${x + r}`
            : `M${x - r},${y - r}L${x + r},${y + r}M${x - r},${y + r}L${x + r},${y - r}` + (item.symbol === 'star' ? `M${x},${y - r}V${y + r}` : '')
      return [`${SVG} path`, { d, fill: 'none', ...stroke(item.line ?? { color: item.fill, width: 0.75, cap: 'flat' }) }]
    }
    default:
      return [`${SVG} rect`, { x: round(x - r), y: round(y - r), width: round(2 * r), height: round(2 * r), ...paint }]
  }
}

/** The spec of one primitive, or null. `clipId` maps the layout's clip names to unique ids. */
function itemSpec(item, clipIds) {
  const clip = item.clip && clipIds[item.clip] ? { 'clip-path': `url(#${clipIds[item.clip]})` } : {}
  switch (item.kind) {
    case 'rect':
      if (!item.fill && !item.line) return null
      return [
        `${SVG} rect`,
        {
          x: round(item.x),
          y: round(item.y),
          width: round(Math.max(0, item.w)),
          height: round(Math.max(0, item.h)),
          fill: item.fill ?? 'none',
          ...stroke(item.line),
          ...clip,
          ...(item.rotate ? { transform: `rotate(${round(item.rotate)} ${round(item.ox)} ${round(item.oy)})` } : {}),
        },
      ]
    case 'line':
      if (!item.line) return null
      return [`${SVG} line`, { x1: round(item.x1), y1: round(item.y1), x2: round(item.x2), y2: round(item.y2), ...stroke(item.line), ...clip }]
    case 'path':
      if (!item.line) return null
      return [
        `${SVG} polyline`,
        { points: item.points.map(([x, y]) => `${round(x)},${round(y)}`).join(' '), fill: 'none', ...stroke(item.line), ...clip },
      ]
    case 'marker':
      return markerSpec(item)
    case 'text':
      if (!item.text || item.style?.color === 'none') return null
      return [`${SVG} text`, textAttrs(item), item.text]
    default:
      return null
  }
}

/**
 * The <svg> spec for a laid-out chart. `label` names it for assistive tech.
 */
export function chartSvgSpec(layout, label = '') {
  const clipIds = {}
  const defs = []
  for (const item of layout.items) {
    if (item.kind !== 'clip') continue
    const id = `studio-chart-clip-${++clipSequence}`
    clipIds[item.id] = id
    defs.push([`${SVG} clipPath`, { id }, [`${SVG} rect`, { x: round(item.x), y: round(item.y), width: round(item.w), height: round(item.h) }]])
  }
  const children = layout.items.map((item) => itemSpec(item, clipIds)).filter(Boolean)
  return [
    `${SVG} svg`,
    {
      class: 'studio-chart-svg',
      width: `${round(layout.width)}pt`,
      height: `${round(layout.height)}pt`,
      viewBox: `0 0 ${round(layout.width)} ${round(layout.height)}`,
      // The frame's stroke straddles the chart's edge, as in Word.
      overflow: 'visible',
      role: 'img',
      'aria-label': label || undefined,
    },
    ...(defs.length ? [[`${SVG} defs`, {}, ...defs]] : []),
    ...children,
  ]
}
